"use client";

import DynamicIcon from "@/components/atoms/DynamicIcon";
import { cn } from "@/lib/utils";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import { Fragment, useMemo } from "react";

/**
 * Key Numbers 1055 — KUK home Stats.
 *
 * Port of kuk-campus-portal `components/Home/Stats.tsx`: a short gradient bar
 * carrying an icon over a thousands-separated count and its label, with a
 * vertical rule between neighbours.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see
 * globals.css `--breakpoint-*`), so every `md:` in the reference is ported as
 * `tab:`.
 *
 * THE ICON SIZE — v1 ships the icon twice, `hidden md:block` at 30px and
 * `md:hidden` at 25px, so exactly one is ever visible. Rendered once here with
 * the size switched in CSS, which is the same result from half the DOM.
 *
 * THE LABEL — v1 prints `stat.title.split(" ")[1]`, i.e. the SECOND word of the
 * title and nothing else: "Total Faculty" renders as "Faculty", and a
 * single-word title renders as nothing at all. That is a display bug, not a
 * design; the label is its own CMS field here and prints in full. Seeded content
 * therefore stores "Faculty", not "Total Faculty", so the live page is
 * reproduced exactly while the widget stays sane.
 *
 * THE `+` SUFFIX — v1 appends a literal "+" to every count. It is a CMS field
 * here so a figure that should not carry it can drop it.
 *
 * ICONS — v1 names its icons from a code-side map ('tutor', 'breifcase',
 * 'student'). `DynamicIcon` accepts both an icon-map key and an uploaded image
 * URL, so those names carry over verbatim and custom art is also possible.
 */

interface StatRow {
  id?: string | number;
  meta_icon?: string;
  meta_icon_color?: string;
  meta_count?: string | number;
  meta_title?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface KeyNumbers1055Data {
  metadata?: StatRow[];
  /** Band gradient. */
  gradient_color_1?: string;
  gradient_color_2?: string;
  /** Rule between neighbours. */
  gradient_color_3?: string;
  /** Count. */
  title_start_color?: string;
  /** Label. */
  description_color?: string;
  /** Appended to every count. v1 hardcodes "+". */
  description?: string;
  status?: boolean;
}

/** v1 `formatNumberWithCommas` — Indian-agnostic thousands grouping. */
function formatCount(value: string | number | undefined): string {
  if (value === undefined || value === null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("en-US");
}

const KeyNumbers1055 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as KeyNumbers1055Data | undefined;

  const {
    gradient_color_1 = "#000000",
    gradient_color_2 = "#1E40AF",
    gradient_color_3 = "#93C5FD",
    title_start_color = "#FFFFFF",
    description_color = "#FFFFFF",
    description = "+",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);

  const stats = useMemo(() => {
    const rows = Array.isArray(sectionData?.metadata) ? sectionData!.metadata! : [];
    return rows
      .filter((r) => r?.meta_status !== false)
      .sort(
        (a, b) => (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  if (!status || stats.length === 0) return null;

  return (
    <section
      className="mx-auto max-h-[160px] w-full p-6"
      style={{
        backgroundImage: `linear-gradient(to right, ${gradient_color_1}, ${gradient_color_2})`,
      }}
    >
      <div className="relative mx-auto w-full max-w-[1120px] overflow-hidden">
        <div className="flex items-center justify-between tab:px-20">
          {stats.map((stat, i) => (
            <Fragment key={stat.id ?? i}>
              <div className="flex flex-1 flex-col items-center">
                {stat.meta_icon && (
                  <span className="[&_img]:size-[25px] [&_svg]:size-[25px] tab:[&_img]:size-[30px] tab:[&_svg]:size-[30px]">
                    <DynamicIcon
                      iconName={stat.meta_icon}
                      color={stat.meta_icon_color || "#FFFFFF"}
                      width="30"
                      height="30"
                    />
                  </span>
                )}
                <p
                  className="mt-2 text-2xl font-semibold"
                  style={{ color: title_start_color }}
                >
                  {formatCount(stat.meta_count)}
                  {description}
                </p>
                {stat.meta_title && (
                  <p
                    className="text-sm font-extralight"
                    style={{ color: description_color }}
                  >
                    {stat.meta_title}
                  </p>
                )}
              </div>
              {i !== stats.length - 1 && (
                <div
                  aria-hidden
                  className={cn("mx-4 h-24 w-px shrink-0")}
                  style={{ backgroundColor: gradient_color_3 }}
                />
              )}
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

export default KeyNumbers1055;
